import Checkbox, { CheckboxProps } from 'components/Checkbox/Checkbox.component';
import { DocumentDropzone } from 'components/DocumentDropzone';
import { DocumentDropzoneProps } from 'components/DocumentDropzone/DocumentDropzone.component';
import { FieldWrapper } from 'components/FieldWrapper';
import Textarea, { TextareaProps } from 'components/Textarea/Textarea.components';
import { Multiplier } from 'models/Multiplier';
import React, { FC, useEffect } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { formatNumber } from 'utils/formatNumber';
import { employeeContractOptions } from './utils';
import { DocumentFormValues } from 'pages/EmployeeProfilePage/components/EmploymentInformation/types/DocumentFormValues';
import SelectV2, { SelectV2Props } from 'components/SelectV2/SelectV2.component';
import { DatePicker, Loader } from 'components';
import useCurrenciesStore from 'store/useCurrenciesStore';
import FormattedNumberInput, {
  FormattedInputProps,
} from 'components/FormattedNumberInput/FormattedNumberInput.component';

import './DocumentForm.styles.scss';

export type DocumentFormProps = {
  methods: UseFormReturn<DocumentFormValues>;
  multipliers: Multiplier[];
};

const DocumentForm: FC<DocumentFormProps> = (props) => {
  const { methods, multipliers } = props;

  const { currencies, isLoading: isCurrencyLoading } = useCurrenciesStore();
  const { defaultCurrency } = currencies || {};

  const grossSalary = () =>
    `${formatNumber(
      (methods.watch('netSalaryAmount') || 0) *
        (multipliers.find((multiplier) => multiplier.id === methods.watch('multiplierId'))?.value ||
          0),
      2,
    )} 
      ${defaultCurrency}`;

  const netSalaryAmount = useWatch({ control: methods.control, name: 'netSalaryAmount' });

  useEffect(() => {
    if (methods.formState.touchedFields.netSalaryAmount) methods.trigger('multiplierId');
  }, [netSalaryAmount]);

  return (
    <div className='document-form'>
      <div className='document-form__row'>
        <FieldWrapper<SelectV2Props>
          name='type'
          fieldComponent={SelectV2}
          options={employeeContractOptions}
          label='Document type'
          placeholder='Select type'
          asterix
        />
      </div>
      <div className='document-form__row document-form__dates'>
        <FieldWrapper<any>
          name='startDate'
          label='Start Date'
          fieldComponent={DatePicker}
          asterix
          minDate={new Date('November 1, 2019 00:00:00')}
        />
        <div className='document-form__end-date'>
          <FieldWrapper<any>
            name='endDate'
            label='End Date'
            fieldComponent={DatePicker}
            disabled={methods.watch('noEndDate')}
            asterix
            minDate={new Date('November 1, 2019 00:00:00')}
          />
          <FieldWrapper<CheckboxProps>
            name='noEndDate'
            key={`end-date-${methods.formState.defaultValues?.noEndDate}`}
            label='No end date'
            fieldComponent={Checkbox}
            className='document-form__no-end-date'
            checked={
              methods.formState.dirtyFields.noEndDate
                ? methods.watch('noEndDate')
                : methods.formState.defaultValues?.noEndDate
            }
          />
        </div>
      </div>
      <div className='document-form__row document-form__salary'>
        <FieldWrapper<FormattedInputProps>
          name='netSalaryAmount'
          fieldComponent={FormattedNumberInput}
          label='Net salary'
          min={0}
          suffixNode={
            <span className='document-form__salary-sufix'>
              {isCurrencyLoading ? <Loader /> : defaultCurrency}
            </span>
          }
        />

        <FieldWrapper<SelectV2Props>
          name='multiplierId'
          label='Multiplier'
          fieldComponent={SelectV2}
          options={multipliers.map((multiplier) => {
            return { label: multiplier.name, value: multiplier.id };
          })}
          placeholder='Choose multiplier'
          asterix={!!netSalaryAmount}
        />
      </div>
      <div className='document-form__row document-form__gross-salary'>
        <b>Gross salary:</b>
        <p>{!isCurrencyLoading ? grossSalary() : <Loader />}</p>
      </div>
      <FieldWrapper<TextareaProps>
        name='note'
        label='Note'
        placeholder='Enter note...'
        fieldComponent={Textarea}
      />
      <div className='document-form__row document-form__doc'>
        <b>Document*</b>
        <FieldWrapper<DocumentDropzoneProps> name='file' fieldComponent={DocumentDropzone} />
      </div>
      {methods.formState.errors.root && (
        <small className='document-form__error-msg'>{methods.formState.errors.root?.message}</small>
      )}
    </div>
  );
};

export default DocumentForm;
